type RegistrationListener = (registration: ServiceWorkerRegistration) => void;

let registration: ServiceWorkerRegistration | undefined;
let registrationPromise: Promise<ServiceWorkerRegistration> | undefined;
const registrationListeners = new Set<RegistrationListener>();

export function subscribeToServiceWorkerRegistration(listener: RegistrationListener) {
  registrationListeners.add(listener);
  if (registration) listener(registration);
  return () => registrationListeners.delete(listener);
}

function registerServiceWorker() {
  if (registrationPromise) return registrationPromise;

  registrationPromise = navigator.serviceWorker.register('/sw.js').then((nextRegistration) => {
    registration = nextRegistration;
    installUpdateChecker(nextRegistration);
    registrationListeners.forEach((listener) => listener(nextRegistration));
    return nextRegistration;
  });

  return registrationPromise;
}

function checkForServiceWorkerUpdate(current: ServiceWorkerRegistration) {
  // update() re-fetches /sw.js (served no-store, so this always sees the latest
  // deploy) and installs it on a byte difference. Offline failures just keep
  // the current shell until the next check.
  return current.update().catch(() => undefined);
}

function installUpdateChecker(current: ServiceWorkerRegistration) {
  // Detection only — this module never activates an update. PWAUpdatePrompt
  // owns activation end-to-end: it shows the Update Now/Later consent card
  // when a worker installs under a running controller, posts SKIP_WAITING to
  // the waiting worker on consent, and reloads on the takeover itself.
  // (This module used to auto-post SKIP_WAITING the instant an update
  // installed, racing the prompt's consent: the page reloaded with the card
  // still on screen, and "Later" could never hold the old build.)

  // Launch check, then re-check whenever the app becomes visible or focused:
  // iOS standalone resumes the suspended webview without a navigation, so the
  // browser's own update-on-navigation check never runs.
  void checkForServiceWorkerUpdate(current);
  const checkWhenVisible = () => {
    if (document.visibilityState === 'visible') void checkForServiceWorkerUpdate(current);
  };
  document.addEventListener('visibilitychange', checkWhenVisible);
  window.addEventListener('focus', checkWhenVisible);
}

export function registerServiceWorkerAfterLoad() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  // Only register in production: the generated /sw.js exists only after a
  // `vite build` (package.json appends generation to `build`, not `dev`), so
  // local Vite would otherwise request a nonexistent file and log a 404 on
  // every page load (issue #914). Gate on VITE_APP_ENV like the admin-login
  // route: staging deploys as a production build (import.meta.env.PROD is
  // true there too), so only the exact 'production' value registers.
  if (import.meta.env.VITE_APP_ENV !== 'production') return;

  // Detection stays automatic; activation is consent-gated (see
  // installUpdateChecker), so no controllerchange handling lives here — a
  // takeover only ever happens because PWAUpdatePrompt posted SKIP_WAITING,
  // or because the last client closed and the waiting worker activated idle.
  const register = () => {
    void registerServiceWorker().catch(() => undefined);
  };

  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register, { once: true });
  }
}
